import { useEffect } from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { trustArcNoticeUrl } from "~/hec/config/trustarc";

interface TrustArcBannerProps {
  attributes?: Record<string, unknown>;
  domain: string;
  script?: string;
}

/**
 * The whole container is hydration-opaque: React never claims, validates, or
 * rewrites anything below `.trust-arc-container`. TrustArc's two mount points
 * are emitted as raw HTML so third-party code (TrustArc itself, consent-
 * blocking extensions, cosmetic-filter lists that target these ids) can write
 * into them, insert siblings before them, or remove them outright without
 * React ever seeing a mismatch.
 *
 * ONE shared reference, never an inline literal: React diffs
 * `dangerouslySetInnerHTML` by object identity, so a fresh object on every
 * render would re-set `innerHTML` on each block re-render and wipe the consent
 * banner TrustArc injected (pinned by the hydration test).
 */
const TRUSTARC_MOUNT_POINTS_HTML = {
  __html: '<div id="consent_blackbar"></div><div id="teconsent"></div>',
};

/**
 * TrustArcBanner component for Builder.io
 *
 * Injects the TrustArc cookie consent script. The original hec-composable version
 * used react-helmet for script injection. In the Workers environment, we use
 * a client-side useEffect to inject the script tag into the document head.
 *
 * COM-184: on FSA/HSA this useEffect is normally a NO-OP — the SSR
 * `TrustArcBootstrapInlineScript` (root.tsx body inline-script run, fed by
 * `TRUSTARC_DOMAINS` in app/hec/config/trustarc.ts) injects the same script
 * (same `id="trustarc"`, byte-identical URL via `trustArcNoticeUrl`) at body
 * parse-time, long before hydration, because the banner text is the homepage
 * LCP element and the post-hydration injection put its paint 19–20s out under
 * throttle. This block remains the injector ONLY in the Builder editor
 * (bootstrap suppressed there) and on sites without a `TRUSTARC_DOMAINS`
 * mapping (WDH). The script URL is built by the shared `trustArcNoticeUrl`
 * helper so the two injectors can never drift apart.
 */
const TrustArcBanner = ({
  attributes,
  domain,
  script,
}: TrustArcBannerProps) => {
  useEffect(() => {
    // Inject the main TrustArc consent script (fallback path — see COM-184
    // note above; usually already present via the SSR bootstrap).
    if (domain && !document.getElementById("trustarc")) {
      const scriptEl = document.createElement("script");
      scriptEl.id = "trustarc";
      scriptEl.type = "text/javascript";
      scriptEl.async = true;
      // Async-injected scripts default to Low fetch priority; the banner
      // paint is the LCP, so hint it up the ladder (COM-184).
      scriptEl.setAttribute("fetchpriority", "high");
      // Shared helper — byte-identical to the SSR bootstrap's URL, so the
      // `id="trustarc"` dedupe always compares like-for-like integrations.
      scriptEl.src = trustArcNoticeUrl(domain);
      document.head.appendChild(scriptEl);
    }

    // Inject optional custom script
    if (script && !document.getElementById("trustarc-script")) {
      const customScriptEl = document.createElement("script");
      customScriptEl.id = "trustarc-script";
      customScriptEl.type = "text/javascript";
      customScriptEl.async = true;
      customScriptEl.textContent = script;
      document.head.appendChild(customScriptEl);
    }

    return () => {
      // Cleanup on unmount
      document.getElementById("trustarc")?.remove();
      document.getElementById("trustarc-script")?.remove();
    };
  }, [domain, script]);

  return (
    <div
      {...(attributes as React.HTMLAttributes<HTMLDivElement>)}
      className="trust-arc-container"
      data-testid="trust-arc-container"
      /* Hydration-opaque container (hotfix v2026.15.01, G1 in the post-release
         error inventory). PR #1125 made the two mount points opaque, which
         covers TrustArc writing INTO them (COM-184 moved its script to body
         parse-time, so it now wins the race against hydration on most page
         loads). Production beacons and a live reproduction on the RC showed
         the remaining #418s come from the container's CHILD LIST changing
         before hydration — a foreign node inserted before `#consent_blackbar`
         or a mount point removed (consent-dismissing extensions / cosmetic
         filters target exactly these ids). React 19 throws #418 for that,
         discards the SSR tree and re-renders it client-side: the multi-second
         dead-click window. Emitting the mount points as raw HTML on the
         container removes React from the subtree entirely; TrustArc still
         targets the ids as before. `suppressHydrationWarning` silences the
         dev-only innerHTML diff when a third party has already mutated it. */
      suppressHydrationWarning
      dangerouslySetInnerHTML={TRUSTARC_MOUNT_POINTS_HTML}
    />
  );
};

export const TrustArcBannerDefinition = {
  name: "TrustArcBanner",
  component: TrustArcBanner,
  override: true,
  shouldReceiveBuilderProps: {
    builderBlock: false,
    builderContext: true,
  },
  static: true,
  isRSC: true,
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/user.svg",
  inputs: [
    {
      name: "domain",
      type: "text",
      required: true,
      autoFocus: true,
      bubble: true,
      defaultValue: "hsastore.com",
    },
    {
      name: "script",
      type: "longText",
      required: false,
      defaultValue: "",
      helperText:
        "Optional custom script to run after the TrustArc banner is loaded",
    },
  ],
} satisfies RegisteredComponent;

export default TrustArcBanner;
